import * as qs from 'query-string';
import type { PickYourFiDeviceKey } from './HardwareFirstBelowFold';

const DEVICE_KEYS: readonly string[] = ['mini', 's3', 'ultra'];

/**
 * `?products=s3,ultra` (or `s3-ultra`) narrows the shop to the trackers it names, for a landing
 * link that sells one device or compares two. Short keys only; tokens it does not know are dropped.
 * Undefined when the param is absent, so the lineup is untouched.
 */
export function productsFromQuery(search: string): PickYourFiDeviceKey[] | undefined {
  const requested = qs.parse(search).products;
  if (typeof requested !== 'string') {
    return undefined;
  }
  const keys = requested
    .toLowerCase()
    .split(/[,-]/)
    .map((token) => token.trim())
    .filter((token): token is PickYourFiDeviceKey => DEVICE_KEYS.includes(token));
  return [...new Set(keys)];
}

/**
 * The trackers the shop opens on when neither the url nor the session names a lineup. Ultra is
 * left out: a bare `/shop` sells Mini and Series 3+. A `?selected=ultra` link (the Ultra page's
 * cta) still gets the full lineup so it lands on Ultra, and `?products=all` shows everything.
 */
export const DEFAULT_SHOP_LINEUP: PickYourFiDeviceKey[] = ['mini', 's3'];

export function defaultShopLineup(selected: PickYourFiDeviceKey | undefined): PickYourFiDeviceKey[] | undefined {
  return selected && !DEFAULT_SHOP_LINEUP.includes(selected) ? undefined : DEFAULT_SHOP_LINEUP;
}

const LINEUP_SESSION_KEY = 'fi_shop_lineup';

/**
 * The shop remembers a `products=` lineup for the session, so the bag's back button, the empty-bag
 * cta and the nav bring the shopper back to the trackers the landing link named. A url with the param
 * replaces it; `?products=all` (or anything naming no tracker) clears it. A bare url changes nothing.
 */
export function rememberShopLineup(search: string): void {
  const requested = productsFromQuery(search);
  if (!requested) {
    return;
  }
  writeLineup(requested.length > 0 ? requested.join(',') : undefined);
}

export function forgetShopLineup(): void {
  writeLineup(undefined);
}

function writeLineup(value: string | undefined): void {
  try {
    if (value) {
      sessionStorage.setItem(LINEUP_SESSION_KEY, value);
    } else {
      sessionStorage.removeItem(LINEUP_SESSION_KEY);
    }
  } catch {
    // storage can be unavailable; the lineup is a convenience, not state
  }
}

export function rememberedShopLineup(): PickYourFiDeviceKey[] | undefined {
  try {
    const stored = sessionStorage.getItem(LINEUP_SESSION_KEY);
    return stored ? productsFromQuery(`?products=${stored}`) : undefined;
  } catch {
    return undefined;
  }
}
